import type { SourceBox } from "@gb/contracts";
import { AuthedFrame, AuthedImage } from "@/components/gb/authed-file";
import { RegionFrame } from "./region-overlay";

/**
 * A file, shown by the browser rather than by a library.
 *
 * Two screens show the same bytes — the document viewer and a Brain entry — and
 * they must not disagree about how. An `<img>` for an image, an `<iframe>` for
 * everything else, and no third case: the decision about *whether* a file can be
 * shown at all was already made server-side and arrives as `type`.
 *
 * `type` is the API's `previewType`, never the document's own `mimeType`. For a
 * `.docx` those differ — the preview is the PDF it was rendered to — and keying
 * off the wrong one shows a "cannot display this" panel for a file that is
 * sitting in the bucket as displayable pages.
 *
 * `box` is the region a look read on a picture file (2026-09-26, Phase 5
 * slice V). It is drawn on the picture here. A PDF's region is drawn on a
 * picture of its page, above the frame (`CitedRegionPanel`), because nothing
 * can draw over the browser's PDF viewer.
 */
export function FilePreview({
  path,
  type,
  filename,
  page,
  box,
  className,
}: {
  /** A byte route behind the guard, which 302s to storage. */
  path: string;
  /** What the route will actually serve, from `previewType`. */
  type: string;
  filename: string;
  /** Opens the PDF here — a Brain hit that says "page 41" means it. */
  page?: number | null;
  /** The region to mark on a picture file. */
  box?: SourceBox | null;
  className?: string;
}) {
  if (type.startsWith("image/") && box) {
    // Not `object-contain`: a letterboxed picture would put the region off
    // the picture by the width of the bars. The picture keeps its own aspect
    // ratio in a block that shrinks to fit it, and the region is drawn on
    // that block (`region-overlay.tsx`).
    return (
      <div className="flex justify-center">
        <RegionFrame box={box}>
          <AuthedImage
            path={path}
            alt={filename}
            className="block h-auto max-h-[78vh] w-auto max-w-full"
            fallbackClassName="h-[320px] w-[320px]"
          />
        </RegionFrame>
      </div>
    );
  }

  if (type.startsWith("image/")) {
    return (
      <AuthedImage
        path={path}
        alt={filename}
        className={className ?? "max-h-[78vh] w-full object-contain"}
        fallbackClassName="h-[320px]"
      />
    );
  }

  return (
    /* An iframe rather than `<embed>` or `<object>`: it is the one every
       browser routes through its built-in PDF viewer, and the only one that
       honours `#page=`. `AuthedFrame` resolves `path` with the bearer token
       first — a bare `src` on a byte route sends no header and gets a 401 the
       browser then blocks. */
    <AuthedFrame
      path={path}
      title={filename}
      fragment={page && type === "application/pdf" ? `page=${page}` : undefined}
      className={className ?? "h-[78vh] w-full border-0 bg-white"}
      // Pinned from the type the API said it would serve, so the blob is typed
      // by us rather than by whatever the storage response asserted.
      type={type}
    />
  );
}
